import { HTMLTooltip } from "@components:HTMLTooltip";
import { getValidator } from "@components:SchemaForm";
import { SuperInputBase } from "@components:SuperInput/SuperInput.Base";
import { Box, Chip, TextField, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import { FountainBlue } from "@themes:palette";
import { camelToSnakeCase } from "@utils:object";
import { defaultTheme, JsonData, JsonEditor } from "json-edit-react";
import { isEmpty, noop } from "lodash";
import { useEffect, useRef, useState } from "react";
import { Controller, Path, PathValue } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { IoMdCheckmarkCircle } from "react-icons/io";
import { MdRedo, MdUndo } from "react-icons/md";
import useUndo from "use-undo";

import { Preference as PreferenceType, PreferenceProps } from "./types";

export function Preference<T extends PreferenceType>({
  control,
  formState,
  setValue,
  watch,
  trigger,
  jsonSchema,
}: PreferenceProps<T>) {
  const { t } = useTranslation();
  // Compiled once per schema object, not on every render
  const validateJsonSchema = getValidator(jsonSchema);
  const [
    { present },
    { set: setData, reset, undo: undoData, redo: redoData, canUndo, canRedo },
  ] = useUndo({});

  const undo = () => undoData();
  const redo = () => redoData();

  useEffect(() => {
    // sync useUndo with react-hook-form,
    // because we update the state of useUndo not directly from the form
    if (!isEmpty(present)) {
      setValue("preference" as Path<T>, present as PathValue<T, Path<T>>, {
        shouldDirty: true,
        shouldValidate: true,
        shouldTouch: true,
      });
    }
  }, [present, setValue]);

  useEffect(() => {
    // validate preference when the component mounts
    const touchPreference = async () => {
      await trigger("preference" as Path<T>);
    };
    touchPreference().then(noop);
  }, [trigger]);

  const preference = watch("preference" as Path<T>);
  const isSetSettingsForUpdate = useRef(false);

  useEffect(() => {
    // when we update the user preference that has preference,
    // we need to set the preference to useUndo to begin keeping track of changes
    if (!isEmpty(preference)) {
      if (isSetSettingsForUpdate.current) {
        return;
      }
      isSetSettingsForUpdate.current = true;

      reset(preference as T);
    }
  }, [preference, reset]);

  const [searchText, setSearchText] = useState("");
  const errorMessage = formState?.errors?.preference?.message;

  return (
    <SuperInputBase label={t("preferences.preference.label")}>
      <Box
        sx={{
          position: "relative",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "10px",
            zIndex: 100,
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            paddingX: "10px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: "10px",
            }}
          >
            {errorMessage ? (
              <HTMLTooltip
                title={
                  <>
                    {(errorMessage as string)
                      ?.split("\n")
                      ?.map((message: string, i: number) => (
                        <Typography
                          key={i}
                          sx={{ display: "block", marginY: "8px" }}
                        >
                          {message}
                        </Typography>
                      ))}
                  </>
                }
              >
                <Chip
                  label={t("preferences.preference.invalid")}
                  sx={{
                    background: "#874b4b" + 40,
                    color: "#874b4b",
                    height: "32px",
                  }}
                  icon={<IoMdCheckmarkCircle size={20} color="white" />}
                />
              </HTMLTooltip>
            ) : (
              <Chip
                label={t("preferences.preference.valid")}
                sx={{
                  background: "#3cbd6a" + 40,
                  color: "#3cbd6a",
                  height: "32px",
                }}
                icon={<IoMdCheckmarkCircle size={20} color="white" />}
              />
            )}
            <Box>
              <Button startIcon={<MdUndo />} disabled={!canUndo} onClick={undo}>
                {t("preferences.preference.undo")}
              </Button>
              <Button startIcon={<MdRedo />} disabled={!canRedo} onClick={redo}>
                {t("preferences.preference.redo")}
              </Button>
            </Box>
          </Box>
          <TextField
            sx={{
              backgroundColor: "#f6f6f6",
              borderRadius: 50,
              width: searchText ? "30%" : "10%",
              height: "30px",
              "&:focus-within": {
                width: "30%",
              },
              transition: "width 0.3s",
              borderColor: searchText ? FountainBlue[600] : undefined,
              "& .MuiOutlinedInput-root": {
                height: "32px",
                borderRadius: 50,
                "&:hover fieldset": {
                  borderColor: FountainBlue[600],
                },
                fieldset: {
                  borderWidth: 2,
                  borderColor: searchText ? FountainBlue[600] : undefined,
                },
              },
            }}
            placeholder="🔍"
            onChange={(e) => setSearchText(e.target.value)}
          />
        </Box>
        <Controller
          name={"preference" as Path<T>}
          control={control}
          rules={{
            validate: (value) => {
              const isValid = validateJsonSchema(
                camelToSnakeCase(value as Record<string, unknown>),
              );
              if (!isValid) {
                return validateJsonSchema.errors
                  ?.map(
                    (error) =>
                      `${error.instancePath}${error.instancePath ? ": " : ""}${error.message}`,
                  )
                  .join("\n");
              }
              return true;
            },
          }}
          render={() => (
            <JsonEditor
              data={present}
              setData={setData as (data: JsonData) => void}
              rootName={"preference"}
              theme={[defaultTheme, {}, { container: { paddingTop: "50px" } }]}
              indent={2}
              showErrorMessages
              collapse={2}
              collapseAnimationTime={300}
              showCollectionCount
              enableClipboard
              restrictDrag
              searchFilter="all"
              searchText={searchText}
              defaultValue={"New data!"}
              showArrayIndices
              showStringQuotes
              minWidth="100%"
              maxWidth="100%"
              stringTruncate={90}
            />
          )}
        />
      </Box>
    </SuperInputBase>
  );
}
